import { useEffect, useRef, useState } from 'react';
import { useTenantMembers, useUserByUid } from '../../api/users.js';
import type { Membership } from '../../api/types.js';

interface Props {
  tenantId: string;
  currentAssigneeUid: string | null | undefined;
  onPick: (uid: string | null) => void;
  onClose: () => void;
  busy: boolean;
}

export function AssignModal({ tenantId, currentAssigneeUid, onPick, onClose, busy }: Props) {
  const members = useTenantMembers(tenantId);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = members.data ?? [];

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="assign-modal"
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="assign-modal-title">
        <header className="modal__head">
          <h2 id="assign-modal-title" className="display modal__title">
            Assigner la facture
          </h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="assign-modal-close"
          >
            ×
          </button>
        </header>

        <input
          ref={inputRef}
          type="search"
          className="modal__search"
          placeholder="Rechercher un membre…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="assign-search"
        />

        <div className="modal__list" role="listbox" aria-label="Membres de la société">
          {members.isLoading ? (
            <div className="muted" style={{ padding: 12 }} data-testid="assign-loading">
              Chargement des membres…
            </div>
          ) : members.isError ? (
            <div className="invoices__error" role="alert">
              Impossible de charger les membres.
            </div>
          ) : list.length === 0 ? (
            <div className="muted" style={{ padding: 12 }}>
              Aucun membre dans cette société.
            </div>
          ) : (
            <MemberList
              members={list}
              query={query}
              currentAssigneeUid={currentAssigneeUid}
              onPick={onPick}
              busy={busy}
            />
          )}
        </div>

        {currentAssigneeUid ? (
          <footer className="modal__foot">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => onPick(null)}
              disabled={busy}
              data-testid="assign-unassign"
            >
              Désassigner
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}

interface MemberListProps {
  members: Membership[];
  query: string;
  currentAssigneeUid: string | null | undefined;
  onPick: (uid: string) => void;
  busy: boolean;
}

function MemberList({ members, query, currentAssigneeUid, onPick, busy }: MemberListProps) {
  const lowerQ = query.trim().toLowerCase();
  return (
    <ul className="assign-members" data-testid="assign-members">
      {members.map((m) => (
        <MemberRow
          key={m.uid}
          membership={m}
          query={lowerQ}
          isCurrent={m.uid === currentAssigneeUid}
          onPick={onPick}
          busy={busy}
        />
      ))}
    </ul>
  );
}

function MemberRow({
  membership,
  query,
  isCurrent,
  onPick,
  busy,
}: {
  membership: Membership;
  query: string;
  isCurrent: boolean;
  onPick: (uid: string) => void;
  busy: boolean;
}) {
  const lookup = useUserByUid(membership.uid);
  const user = lookup.data ?? null;
  const displayName = user?.displayName ?? null;
  const email = user?.email ?? null;
  const haystack = `${displayName ?? ''} ${email ?? ''} ${membership.uid}`.toLowerCase();
  const matches = !query || haystack.includes(query);
  if (!matches) return null;
  const initials = computeInitials(displayName, email, membership.uid);
  return (
    <li
      className={`assign-member${isCurrent ? ' assign-member--current' : ''}`}
      role="option"
      aria-selected={isCurrent}
      data-testid={`assign-member-${membership.uid}`}
    >
      <button
        type="button"
        className="assign-member__btn"
        onClick={() => onPick(membership.uid)}
        disabled={busy || isCurrent}
        title={isCurrent ? 'Déjà assigné' : 'Assigner à ce membre'}
      >
        <span className="assign-member__avatar">{initials}</span>
        <span className="assign-member__main">
          <span className="assign-member__name">{displayName ?? email ?? membership.uid}</span>
          {email && displayName ? (
            <span className="assign-member__email muted">{email}</span>
          ) : null}
        </span>
        <span className="assign-member__role muted">{membership.role}</span>
      </button>
    </li>
  );
}

function computeInitials(displayName: string | null, email: string | null, uid: string): string {
  const src = displayName ?? email ?? uid;
  const parts = src.split(/[\s.@-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
